import { UseFieldArrayInsert } from 'react-hook-form';
import {
  useAdditionalFormContext,
  useFormFieldArray,
} from '../../AdditionalFormContext';
import { useCallback, useEffect, useRef } from 'react';
import {
  useFormTableName,
  useFormTableSequenceNumberColumn,
} from './TanstackFormTable';
import { useUpdateSequenceNumbersRef } from './useUpdateSequenceNumbersRef';

export const useInsertFormTableRow = () => {
  const name = useFormTableName();
  const { insert } = useFormFieldArray(name);
  return useInsertTableRow(name, insert);
};

/**
 * Access insert row method inside of form but outside of table where table name is not in context.<br/>
 *
 * @param name Table attribute name
 * @param ignoreSequence Flag indicating if insert should check existence of sequence field
 * @return method for inserting row to a table
 */
export const useInsertFormRow = (name: string, ignoreSequence = false) => {
  const { getFieldArray } = useAdditionalFormContext();
  const fieldArray = getFieldArray(name);
  const { insert } = fieldArray ?? {};
  return useInsertTableRow(name, insert, ignoreSequence);
};

const useInsertTableRow = <RowData>(
  name: string,
  insert: UseFieldArrayInsert<any, any>,
  ignoreSequence = false
) => {
  const { hasSequenceNumberColumn, sequenceNumberFieldName } =
    useFormTableSequenceNumberColumn(ignoreSequence);
  const updateSequenceNumbers = useUpdateSequenceNumbersRef(
    name,
    sequenceNumberFieldName
  );
  const methodRef =
    useRef<(index: number, rowData: RowData) => void | undefined>(undefined);

  useEffect(() => {
    methodRef.current = (index: number, rowData: RowData) => {
      insert(index, rowData);

      if (hasSequenceNumberColumn) {
        updateSequenceNumbers.current(index);
      }
    };
  }, [
    insert,
    hasSequenceNumberColumn,
    sequenceNumberFieldName,
    updateSequenceNumbers,
  ]);

  return useCallback(
    (index: number, rowData: RowData) => {
      if (!methodRef.current) {
        throw new Error(`Field array "${name}" not found`);
      }
      methodRef.current(index, rowData);
    },
    [methodRef, name]
  );
};
